import ProfileDashboardHeader from "../../features/profile/components/dashboard/ProfileDashboardHeader";
import styles from "./shell.module.css";

/**
 * **Mandatory template for every new platform page** under AppShell.
 *
 * Brand stays in TopHeader. Page chrome is the **transparent pill** header:
 * Back · optional leading icon · green title · circular + FAB (`end`).
 *
 * Typography — **Geist + Geist Mono only** (TRA tokens; no third face):
 * - Header title → Geist body, light weight (300), theme green, Title Case
 * - Section kickers / chips / badges in page body → Geist Mono
 *
 * ```jsx
 * import { PlatformHubCreateButton, PlatformPageShell } from "src/design/shell";
 *
 * <PlatformPageShell
 *   title="My Organisation"
 *   headerIcon="users"
 *   backFallback="/profile"
 *   end={<PlatformHubCreateButton aria-label="Create" onClick={…} />}
 * >
 *   {children}
 * </PlatformPageShell>
 * ```
 *
 * Legacy green-wash pill: `headerVariant="pill"` (deprecated).
 *
 * @param {{
 *   children: import("react").ReactNode,
 *   title?: string,
 *   subtitle?: string,
 *   headerIcon?: string,
 *   backFallback?: string,
 *   onBack?: () => void,
 *   showBack?: boolean,
 *   end?: import("react").ReactNode,
 *   testId?: string,
 *   narrow?: boolean,
 *   headerVariant?: "pill" | "flat",
 * }} props
 */
export default function PlatformPageShell({
  children,
  title,
  subtitle,
  headerIcon,
  backFallback = "/",
  onBack,
  showBack = true,
  end,
  testId,
  narrow = false,
  headerVariant = "flat",
}) {
  const isFlat = headerVariant !== "pill";

  return (
    <div
      className={narrow ? styles.platformPageNarrow : styles.platformPage}
      data-testid={testId || "platform-page-shell"}
      data-header-variant={isFlat ? "flat" : "pill"}
    >
      {isFlat ? (
        <>
          <div className={styles.platformPageIntro}>
            <ProfileDashboardHeader
              title={title}
              subtitle={subtitle}
              headerIcon={headerIcon}
              backFallback={backFallback}
              onBack={onBack}
              showBack={showBack}
              end={end}
              variant="flat"
            />
          </div>
          <div className={styles.platformPageBody}>{children}</div>
        </>
      ) : (
        <>
          <div className={styles.platformPageBack}>
            <ProfileDashboardHeader
              title={title}
              subtitle={subtitle}
              backFallback={backFallback}
              onBack={onBack}
              showBack={showBack}
              end={end}
              variant="pill"
            />
          </div>
          <div className={styles.platformPageBody}>{children}</div>
        </>
      )}
    </div>
  );
}
